import type { Notification } from '../generated/graphql';
import { inboxThreadRoute } from './chatRecordRoute';

/**
 * Determines the navigation route for a notification based on its category and metadata
 * @param notification The notification object
 * @returns The route path or null if not clickable
 */
export function getNotificationRoute(
  notification: Partial<Notification> | null | undefined
): string | null {
  if (!notification) return null;
  const { category, actionUrl, type } = notification;

  if (type === 'AUTH_NEW_DEVICE_LOGIN') {
    return '/settings';
  }

  let metadata: Record<string, unknown> | null = null;
  if (notification.metadata) {
    if (typeof notification.metadata === 'string') {
      try {
        metadata = JSON.parse(notification.metadata) as Record<string, unknown>;
      } catch {
        metadata = {};
      }
    } else if (typeof notification.metadata === 'object') {
      metadata = notification.metadata as Record<string, unknown>;
    }
  }

  let dataObj: Record<string, unknown> | null = null;
  if (notification.data) {
    if (typeof notification.data === 'string') {
      try {
        dataObj = JSON.parse(notification.data) as Record<string, unknown>;
      } catch {
        dataObj = {};
      }
    } else if (typeof notification.data === 'object') {
      dataObj = notification.data as Record<string, unknown>;
    }
  }

  let invoiceId = (metadata?.invoiceId ||
    metadata?.invoice_id ||
    dataObj?.invoiceId ||
    dataObj?.invoice_id) as string | undefined;
  const paymentId = (metadata?.paymentId ||
    metadata?.payment_id ||
    dataObj?.paymentId ||
    dataObj?.payment_id) as string | undefined;
  let jobId = (metadata?.jobId || metadata?.job_id || dataObj?.jobId || dataObj?.job_id) as
    | string
    | undefined;
  let estimateId = (metadata?.estimateId ||
    metadata?.estimate_id ||
    dataObj?.estimateId ||
    dataObj?.estimate_id) as string | undefined;
  const appointmentId = (metadata?.appointmentId ||
    metadata?.appointment_id ||
    dataObj?.appointmentId ||
    dataObj?.appointment_id) as string | undefined;
  const timesheetId = (metadata?.timesheetId ||
    metadata?.timesheet_id ||
    dataObj?.timesheetId ||
    dataObj?.timesheet_id) as string | undefined;
  const productId = (metadata?.productId ||
    metadata?.product_id ||
    dataObj?.productId ||
    dataObj?.product_id) as string | undefined;
  const userId = (metadata?.userId || metadata?.user_id || dataObj?.userId || dataObj?.user_id) as
    | string
    | undefined;
  const crewId = (metadata?.crewId || metadata?.crew_id || dataObj?.crewId || dataObj?.crew_id) as
    | string
    | undefined;
  const chatId = (metadata?.chatId || metadata?.chat_id || dataObj?.chatId || dataObj?.chat_id) as
    | string
    | undefined;

  let actionPath: string | null = null;
  if (actionUrl) {
    if (actionUrl.startsWith('http://') || actionUrl.startsWith('https://')) {
      try {
        const parsed = new URL(actionUrl);
        actionPath = `${parsed.pathname}${parsed.search}${parsed.hash}`;
      } catch {
        actionPath = actionUrl;
      }
    } else {
      actionPath = actionUrl;
    }

    if (!jobId && actionPath) {
      const match = actionPath.match(/\/jobs\/([a-zA-Z0-9_-]+)/);
      if (match) jobId = match[1];
    }
    if (!estimateId && actionPath) {
      const match = actionPath.match(/\/estimates\/([a-zA-Z0-9_-]+)/);
      if (match) estimateId = match[1];
    }
    if (!invoiceId && actionPath) {
      const match = actionPath.match(/\/invoices\/([a-zA-Z0-9_-]+)/);
      if (match) invoiceId = match[1];
    }
  }

  if (
    type === 'JOB_COMPLETED' ||
    (typeof type === 'string' && type.includes('JOB')) ||
    category === 'TRANSACTIONAL_JOB'
  ) {
    if (jobId) return `/jobs/${jobId}`;
  }

  switch (category) {
    case 'TRANSACTIONAL_FINANCIAL':
      if (invoiceId) {
        if (paymentId) {
          return `/invoices/${invoiceId}?payment=${paymentId}`;
        }
        return `/invoices/${invoiceId}`;
      }
      break;

    case 'TRANSACTIONAL_JOB':
      if (jobId) {
        return `/jobs/${jobId}`;
      }
      break;

    case 'TRANSACTIONAL_ESTIMATE':
      if (estimateId) {
        return `/estimates/${estimateId}`;
      }
      break;

    case 'TRANSACTIONAL_APPOINTMENT':
      if (appointmentId) {
        return `/appointments/${appointmentId}`;
      }
      break;

    case 'OPERATIONAL_TIMESHEET':
      if (timesheetId) {
        return `/timesheets/${timesheetId}`;
      }
      break;

    case 'OPERATIONAL_INVENTORY':
      return productId ? `/inventory-products/${productId}` : '/inventory-products';

    case 'OPERATIONAL_TEAM':
      if (userId) return `/users/${userId}`;
      if (crewId) return `/crews/${crewId}`;
      return '/crews';

    case 'COMMUNICATION_MESSAGE':
      // The thread, not the inbox at large. Where a thread opens depends on
      // the chat itself, which this does not have: see getNotificationChatId.
      // `chatId` here is develop's wider read: metadata or data, chatId or
      // chat_id. The route is this branch's, `/inbox/:chatId`, which is what
      // the rebuilt inbox reads with useParams; nothing reads `?chat=`.
      return chatId ? inboxThreadRoute(chatId) : '/inbox';

    case 'COMMUNICATION_FEEDBACK':
      if (jobId) return `/jobs/${jobId}`;
      break;

    case 'SYSTEM_ANNOUNCEMENT':
      return notification.actionUrl || null;

    case 'TRANSACTIONAL_AUTH':
      return notification.actionUrl || '/settings';

    case 'INTEGRATION_STRIPE':
    case 'SUBSCRIPTION_BILLING':
    default:
      break;
  }

  return actionPath || null;
}

/**
 * The chat a message notification is about, or null when the notification is
 * about something else.
 *
 * The route above can only name the inbox: the payload says which chat, never
 * which job, estimate, invoice or appointment that chat belongs to. The caller
 * resolves the chat (see `useChatDestination`) and opens the record's page
 * instead, leaving the inbox for direct conversations, which have no record.
 */
export function getNotificationChatId(notification: Notification): string | null {
  if (notification.category !== 'COMMUNICATION_MESSAGE') return null;
  return notification.metadata?.chatId ?? null;
}
